import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, DollarSign, Calendar, FileText, Truck } from 'lucide-react';
import saasApiService from '../../../services/saasApi';

const schema = yup.object({
  vehicle_id: yup.number().required('Vehículo es requerido').min(1, 'Seleccione un vehículo'),
  amount: yup.number().required('Monto es requerido').min(0.01, 'Monto debe ser mayor a 0'),
  expense_type: yup.string().required('Tipo de gasto es requerido'),
  description: yup.string().required('Descripción es requerida').min(5, 'Mínimo 5 caracteres'),
  expense_date: yup.string().required('Fecha de gasto es requerida'),
  fuel_liters: yup.number().optional().min(0, 'Litros debe ser positivo'),
  odometer_reading: yup.number().optional().min(0, 'Lectura del odómetro debe ser positiva'),
  tire_tread_mm: yup
    .number()
    .optional()
    .min(0, 'La profundidad no puede ser negativa')
    .max(22, 'La profundidad no debe exceder 22 mm'),
  maintenance_category: yup
    .string()
    .oneOf(['engine_oil','filters','gearbox_oil','crown_oil','valve_calibration',''], 'Categoría inválida')
    .optional(),
  supplier: yup.string().optional(),
  notes: yup.string().optional()
});

type MaintenanceCategory = 'engine_oil' | 'filters' | 'gearbox_oil' | 'crown_oil' | 'valve_calibration' | '' | undefined;

interface ExpenseFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  expense?: any;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({ isOpen, onClose, onSave, expense }) => {
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(schema)
  });

  const expenseType = watch('expense_type');
  const showOdometer = ['fuel', 'maintenance', 'repairs'].includes(expenseType);
  const selectedVehicleId = watch('vehicle_id');
  const odometer = watch('odometer_reading');
  const maintenanceCategory = watch('maintenance_category');

  useEffect(() => {
    if (isOpen) {
      fetchVehicles();
      if (expense) {
        setValue('vehicle_id', expense.vehicle_id || '');
        setValue('amount', expense.amount || '');
        setValue('expense_type', expense.expense_type || '');
        setValue('description', expense.description || '');
        setValue('expense_date', expense.expense_date ? expense.expense_date.split('T')[0] : '');
        setValue('fuel_liters', expense.fuel_liters || '');
        setValue('odometer_reading', expense.odometer_reading || '');
        setValue('maintenance_category', expense.maintenance_category || '');
        setValue('supplier', expense.supplier || '');
        setValue('notes', expense.notes || '');
      } else {
        reset();
        // Establecer fecha de gasto por defecto a hoy
        setValue('expense_date', new Date().toISOString().split('T')[0]);
      }
    }
  }, [isOpen, expense, setValue, reset]);

  const fetchVehicles = async () => {
    try {
      const response = await saasApiService.getVehicles();
      console.log('Vehicles response in ExpenseForm:', response);
      
      // Manejar diferentes estructuras de respuesta
      const vehiclesData = Array.isArray(response) ? response : 
                          (response?.vehicles && Array.isArray(response.vehicles)) ? response.vehicles :
                          (response?.data?.vehicles && Array.isArray(response.data.vehicles)) ? response.data.vehicles :
                          (response?.items && Array.isArray(response.items)) ? response.items :
                          (response?.data?.items && Array.isArray(response.data.items)) ? response.data.items : [];
      
      setVehicles(vehiclesData);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      
      if (expense) {
        await saasApiService.updateExpense(expense.id, data);
      } else {
        await saasApiService.createExpense(data);
      }
      
      onSave();
      onClose();
      reset();
    } catch (error) {
      console.error('Error saving expense:', error);
      alert('Error al guardar el gasto');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {expense ? 'Editar Gasto' : 'Nuevo Gasto'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                <Truck className="inline h-4 w-4 mr-1" />
                Vehículo *
              </label>
              <select
                {...register('vehicle_id')}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar vehículo</option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.brand} {vehicle.model} - {vehicle.license_plate}
                  </option>
                ))}
              </select>
              {errors.vehicle_id && (
                <p className="mt-1 text-sm text-red-600">{errors.vehicle_id.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Tipo de Gasto *
              </label>
              <select
                {...register('expense_type')}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar tipo</option>
                <option value="fuel">Combustible</option>
                <option value="maintenance">Mantenimiento</option>
                <option value="insurance">Seguro</option>
                <option value="registration">Registro/Matrícula</option>
                <option value="repairs">Reparaciones</option>
                <option value="tires">Neumáticos</option>
                <option value="other">Otro</option>
              </select>
              {errors.expense_type && (
                <p className="mt-1 text-sm text-red-600">{errors.expense_type.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                <DollarSign className="inline h-4 w-4 mr-1" />
                Monto *
              </label>
              <input
                {...register('amount')}
                type="number"
                step="0.01"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
              {errors.amount && (
                <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                <Calendar className="inline h-4 w-4 mr-1" />
                Fecha de Gasto *
              </label>
              <input
                {...register('expense_date')}
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.expense_date && (
                <p className="mt-1 text-sm text-red-600">{errors.expense_date.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              <FileText className="inline h-4 w-4 mr-1" />
              Descripción *
            </label>
            <input
              {...register('description')}
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Descripción del gasto"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

          {/* Campos específicos: combustible y odómetro */}
          {expenseType === 'fuel' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Litros de Combustible
                </label>
                <input
                  {...register('fuel_liters', { setValueAs: (v) => (v === '' || v == null ? undefined : parseFloat(v)) })}
                  type="number"
                  step="0.01"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                />
                {errors.fuel_liters && (
                  <p className="mt-1 text-sm text-red-600">{errors.fuel_liters.message}</p>
                )}
              </div>

              {/* Odómetro para combustible */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Lectura del Odómetro (km)
                </label>
                <input
                  {...register('odometer_reading', { setValueAs: (v) => (v === '' || v == null ? undefined : parseFloat(v)) })}
                  type="number"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
                {errors.odometer_reading && (
                  <p className="mt-1 text-sm text-red-600">{errors.odometer_reading.message}</p>
                )}
              </div>
            </div>
          )}

          {/* Odómetro para mantenimiento y reparaciones */}
          {showOdometer && expenseType !== 'fuel' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Lectura del Odómetro (km)
              </label>
              <input
                {...register('odometer_reading', { setValueAs: (v) => (v === '' || v == null ? undefined : parseFloat(v)) })}
                type="number"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="0"
              />
              {errors.odometer_reading && (
                <p className="mt-1 text-sm text-red-600">{errors.odometer_reading.message}</p>
              )}
            </div>
          )}

          {/* Profundidad de llanta para mantenimiento */}
          {expenseType === 'maintenance' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Profundidad de llanta (mm)</label>
              <input
                {...register('tire_tread_mm', { setValueAs: (v) => (v === '' || v == null ? undefined : parseFloat(v)) })}
                type="number"
                step="0.1"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ej. 22.0 (nuevo) ... 4.0 (reencauche)"
              />
              {errors.tire_tread_mm && (
                <p className="mt-1 text-sm text-red-600">{errors.tire_tread_mm.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Nuevo: 22 mm. Reencauche: 4 mm. Cada mantenimiento acumula km hasta alcanzar 4 mm.</p>
              {/* Selector de tipo de mantenimiento */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">Tipo de Mantenimiento</label>
                <select
                  {...register('maintenance_category', { setValueAs: (v) => (v === '' ? undefined : v) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seleccionar tipo</option>
                  <option value="engine_oil">Cambio de aceite (Motor)</option>
                  <option value="filters">Filtros</option>
                  <option value="gearbox_oil">Aceite de caja</option>
                  <option value="crown_oil">Aceite de corona</option>
                  <option value="valve_calibration">Calibración de válvulas</option>
                </select>
              </div>

              {/* Info: km desde último y km restantes */}
              {selectedVehicleId && maintenanceCategory && (
                (() => {
                  const v = vehicles.find((x:any) => x.id === Number(selectedVehicleId));
                  if (!v) return null;
                  const maps:any = {
                    engine_oil: { last: v.maint_engine_oil_last_odo, interval: v.maint_engine_oil_interval_km },
                    filters: { last: v.maint_filters_last_odo, interval: v.maint_filters_interval_km },
                    gearbox_oil: { last: v.maint_gearbox_oil_last_odo, interval: v.maint_gearbox_oil_interval_km },
                    crown_oil: { last: v.maint_crown_oil_last_odo, interval: v.maint_crown_oil_interval_km },
                    valve_calibration: { last: v.maint_valve_calib_last_odo, interval: v.maint_valve_calib_interval_km }
                  };
                  const key = (maintenanceCategory || '') as 'engine_oil' | 'filters' | 'gearbox_oil' | 'crown_oil' | 'valve_calibration' | '';
                  if (!key || !(key in maps)) return null;
                  const cfg = maps[key as 'engine_oil' | 'filters' | 'gearbox_oil' | 'crown_oil' | 'valve_calibration'];
                  if (!cfg) return null;
                  const last = parseFloat(cfg.last || 0);
                  const curr = parseFloat(odometer || v.mileage || 0);
                  const traveled = last ? Math.max(0, curr - last) : 0;
                  const remaining = cfg.interval ? Math.max(0, cfg.interval - traveled) : undefined;
                  return (
                    <div className="mt-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-3">
                      <div>Último mantenimiento: {last ? `${last.toLocaleString()} km` : '—'}</div>
                      <div>Km desde último: {traveled.toLocaleString()} km</div>
                      {remaining !== undefined && <div>Faltan: {remaining.toLocaleString()} km</div>}
                    </div>
                  );
                })()
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Proveedor
            </label>
            <input
              {...register('supplier')}
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Nombre del proveedor"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Notas
            </label>
            <textarea
              {...register('notes')}
              rows={3}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Notas adicionales..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Guardando...' : (expense ? 'Actualizar' : 'Crear Gasto')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseForm;
